/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {ProfilePID, ProfileYMMEF} from '@ion/core/src/types/entities';
import {FormState} from '@ion/dls/src/hooks/useFormState';
import {Validators} from '../validation/Validators';

type PIDFieldConfig = {
  required: boolean;
  value?: string | null;
  pattern?: RegExp;
  defaultValue?: string;
};

export enum VehicleProfileFormFields {
  lookupByVin = 'lookupByVin',
  yearString = 'yearString',
  modelString = 'modelString',
  makeString = 'makeString',
  engineString = 'engineString',
  fuelTypeString = 'fuelTypeString'
};

export enum PIDFormFields {
  busProfileName = 'busProfileName',
  busVersionName = 'busVersionName',
  customerRestrictionId = 'customerRestrictionId',
  busProfileId = 'busProfileId',
  byteLength = 'byteLength',
  bitLength = 'bitLength',
  byteStartPosition = 'byteStartPosition',
  bitStartPosition = 'bitStartPosition',
  busTypeName = 'busTypeName',
  busSpeed = 'busSpeed',
  busTxHex = 'busTxHex',
  busRxHex = 'busRxHex',
  initializeMessageHex = 'initializeMessageHex',
  keepAliveMessageHex = 'keepAliveMessageHex',
  exitMessageHex = 'exitMessageHex',
  linearTransformationSlopeNumerator = 'linearTransformationSlopeNumerator',
  linearTransformationSlopeDenominator = 'linearTransformationSlopeDenominator',
  linearTransformationYIntercept = 'linearTransformationYIntercept',
  firmwareParameterName = 'firmwareParameterName',
  formulaName = 'formulaName',
  requestMessageHex = 'requestMessageHex',
  requestMessageExtendedHex = 'requestMessageExtendedHex',
  replyTimeout = 'replyTimeoutCentiSecond',
  extendedRequestIdentifierHex = 'extendedRequestIdentifierHex',
  extendedReplyIdentifierHex = 'extendedReplyIdentifierHex',
  busAuthorizationKeyName = 'busAuthorizationKeyName',
  busTypeExtensionName = 'busTypeExtensionName',
};

export const UNKNOWN = 'UNKNOWN';

/**
 * Defines a constant for unknown year string.
 * 
 * Some manufacturers do not provide a specific year for certain vehicle models,
 * instead using a range of years (e.g., "2015-2017"). To handle such cases,
 * we define a constant to represent an unknown year if the year string is 
 * empty or not provided.
 */
export const UNKNOWN_YEAR = UNKNOWN;
export const UNKNOWN_YEAR_ID = 255;
export const UNKNOWN_ENGINE_ID = 4294967295;
export const UNKNOWN_FUEL_TYPE_ID = 15;
export const YEAR_ID_OFFSET = 1980;

export const getSuccessMessage = (operation): string => (
  translate('vehicle-profile:editors.successMessage', {
    operation : translate(`common:operations.${operation}`)
  })
);

export const DEFAULTS = {
  hex12 : '0x0000000000',
  hex10 : '0x00000000',
  hex4  : '0x00',
  num   : (val: number) => `${val}`
};

const withValidation = (
  value: string | number | null,
  {required = true, pattern = undefined, helperText = ''} = {}
) => ({
  error : false,
  helperText,
  required,
  pattern,
  value : value == null ? '' : String(value)
});

const defaultProp = {required: true, value: '' as string | ''};

export const getInitialState = (vehicleYMMEF: ProfileYMMEF) => {
  return {
    ...vehicleYMMEF,
    [VehicleProfileFormFields.yearString]     : defaultProp,
    [VehicleProfileFormFields.modelString]    : defaultProp,
    [VehicleProfileFormFields.makeString]     : defaultProp,
    [VehicleProfileFormFields.engineString]   : defaultProp,
    [VehicleProfileFormFields.fuelTypeString] : defaultProp
  };
};

export const serializeYMMEF = (formFields: FormState): ProfileYMMEF => {
  const serialized = {} as ProfileYMMEF;
  for (const [key, {value}] of Object.entries(formFields)) {
    if (key === 'yearString') {
      if (value?.toLowerCase() === 'unknown') {
        serialized.yearString = UNKNOWN_YEAR;
        continue;
      }
    }
    serialized[key] = value;
  }
  return serialized;
};

export const mapYMMEFToApi = (s: any) => ({
  YearString        : s?.yearString,
  MakeString        : s?.makeString,
  ModelString       : s?.modelString,
  EngineString      : s?.engineString ?? '',
  FuelString        : s?.fuelTypeString ?? '',
  cloneFromPrevious : !!s?.cloneFromPrevious
});

const {
  busProfileName,
  busVersionName,
  customerRestrictionId,
  byteLength,
  bitLength,
  byteStartPosition,
  bitStartPosition,
  busTypeName,
  busTypeExtensionName,
  busAuthorizationKeyName,
  busSpeed,
  busTxHex,
  busRxHex,
  initializeMessageHex,
  keepAliveMessageHex,
  exitMessageHex,
  linearTransformationSlopeNumerator,
  linearTransformationSlopeDenominator,
  linearTransformationYIntercept,
  firmwareParameterName,
  formulaName,
  requestMessageHex,
  requestMessageExtendedHex,
  replyTimeout,
  extendedRequestIdentifierHex,
  extendedReplyIdentifierHex
} = PIDFormFields;

const {
  required,
  number,
  hex2,
  hex10,
  hex12
} = translate(
  'vehicle-profile:editors.validation',
  {returnObjects: true}
);

export const getInitialPIDState = () => ({
  // General
  [firmwareParameterName] : withValidation('Vehicle Speed', {
    required   : true,
    helperText : required
  }),
  [busProfileName]        : withValidation('', {required: false}),
  [busVersionName]        : withValidation('', {required: false}),
  [customerRestrictionId] : withValidation(0, {required: false}),
  
  // Bus
  [busTypeName]          : withValidation('CAN 6-14', {required: true, helperText: required}),
  [busTypeExtensionName] : withValidation('NotApplicable', {
    required   : true,
    helperText : required
  }),
  [busAuthorizationKeyName] : withValidation('NotApplicable', {
    required   : true,
    helperText : required
  }),
  [busSpeed] : withValidation(DEFAULTS.num(500000), {
    ...Validators.NUMERIC,
    helperText : number
  }),
  
  // Data
  [byteLength]        : withValidation(DEFAULTS.num(1), {...Validators.NUMERIC, helperText: number}),
  [bitLength]         : withValidation(DEFAULTS.num(1), {...Validators.NUMERIC, helperText: number}),
  [byteStartPosition] : withValidation(DEFAULTS.num(0), {
    ...Validators.NUMERIC,
    helperText : number
  }),
  [bitStartPosition] : withValidation(DEFAULTS.num(0), {
    ...Validators.NUMERIC,
    helperText : number
  }),
  
  // Communication
  [busTxHex]             : withValidation(DEFAULTS.hex10, {...Validators.HEX_10, helperText: hex10}),
  [busRxHex]             : withValidation(DEFAULTS.hex10, {...Validators.HEX_10, helperText: hex10}),
  [initializeMessageHex] : withValidation(DEFAULTS.hex10, {
    ...Validators.HEX_10,
    helperText : hex10
  }),
  [keepAliveMessageHex] : withValidation(DEFAULTS.hex10, {
    ...Validators.HEX_10,
    helperText : hex10
  }),
  [exitMessageHex]    : withValidation(DEFAULTS.hex10, {...Validators.HEX_10, helperText: hex10}),
  [requestMessageHex] : withValidation(DEFAULTS.hex10, {
    ...Validators.HEX_10,
    helperText : hex10
  }),
  [requestMessageExtendedHex] : withValidation(DEFAULTS.hex12, {
    ...Validators.HEX_12,
    helperText : hex12
  }),
  [extendedRequestIdentifierHex] : withValidation(DEFAULTS.hex4, {
    ...Validators.HEX_4,
    helperText : hex2
  }),
  [extendedReplyIdentifierHex] : withValidation(DEFAULTS.hex4, {
    ...Validators.HEX_4,
    helperText : hex2
  }),
  [replyTimeout] : withValidation(DEFAULTS.num(5), {...Validators.NUMERIC, helperText: number}),
  
  // Transformation
  [formulaName]                        : withValidation('Straight', {required: false}),
  [linearTransformationSlopeNumerator] : withValidation(DEFAULTS.num(1), {
    required   : false,
    ...Validators.NUMERIC,
    helperText : number
  }),
  [linearTransformationSlopeDenominator] : withValidation(DEFAULTS.num(1), {
    required   : false,
    ...Validators.NUMERIC,
    helperText : number
  }),
  [linearTransformationYIntercept] : withValidation(DEFAULTS.num(0), {
    required   : false,
    ...Validators.NUMERIC,
    helperText : number
  })
});

const NUMERIC_KEYS = new Set<string>([
  'byteLength',
  'bitLength',
  'byteStartPosition',
  'bitStartPosition',
  'busSpeed',
  'replyTimeoutCentiSecond',
  'linearTransformationSlopeNumerator',
  'linearTransformationSlopeDenominator',
  'linearTransformationYIntercept',
  'customerRestrictionId'
]);

const toNumberOrNull = (v: unknown) => {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const serializePIDFields = (
  pidFormFields: FormState | null | undefined
): ProfilePID => {
  const serialized = {} as ProfilePID;
  for (const [key, entry] of Object.entries(pidFormFields ?? {})) {
    const raw =
      entry && typeof entry === 'object' && 'value' in (entry as any)
        ? (entry as any).value
        : entry;
    (serialized as any)[key] = NUMERIC_KEYS.has(key) ? toNumberOrNull(raw) : raw;
  }
  return serialized;
};

const deserializeField = ({
  fieldName,
  value,
  required = true,
  validator = {}
}) => ({
  ...withValidation(value, {required, ...validator}),
  name : fieldName
});

export const deserialize = (profile: Partial<ProfilePID> | null | undefined) => {
  return {
    [PIDFormFields.busProfileName] : deserializeField({
      fieldName : PIDFormFields.busProfileName,
      value     : profile?.busProfileName
    }),
    [PIDFormFields.busVersionName] : deserializeField({
      fieldName : PIDFormFields.busVersionName,
      value     : profile?.busVersionName
    }),
    [PIDFormFields.customerRestrictionId] : deserializeField({
      fieldName : PIDFormFields.customerRestrictionId,
      value :
        profile?.customerRestrictionId != null
          ? String(profile.customerRestrictionId)
          : 0
    }),
    [PIDFormFields.byteLength] : deserializeField({
      fieldName : PIDFormFields.byteLength,
      value :
        profile?.byteLength != null ? String(profile.byteLength) : '',
      validator : {...Validators.NUMERIC, helperText: number}
    }),
    [PIDFormFields.bitLength] : deserializeField({
      fieldName : PIDFormFields.bitLength,
      value :
        profile?.bitLength != null ? String(profile.bitLength) : '',
      validator : {...Validators.NUMERIC, helperText: number}
    }),
    [PIDFormFields.byteStartPosition] : deserializeField({
      fieldName : PIDFormFields.byteStartPosition,
      value :
        profile?.byteStartPosition != null
          ? String(profile.byteStartPosition)
          : '',
      validator : {...Validators.NUMERIC, helperText: number}
    }),
    [PIDFormFields.bitStartPosition] : deserializeField({
      fieldName : PIDFormFields.bitStartPosition,
      value :
        profile?.bitStartPosition != null
          ? String(profile.bitStartPosition)
          : '',
      validator : {...Validators.NUMERIC, helperText: number}
    }),
    [PIDFormFields.busTypeName] : deserializeField({
      fieldName : PIDFormFields.busTypeName,
      value     : profile?.busTypeName
    }),
    [PIDFormFields.busSpeed] : deserializeField({
      fieldName : PIDFormFields.busSpeed,
      value :
        profile?.busSpeed != null ? String(profile.busSpeed) : '',
      validator : {...Validators.NUMERIC, helperText: number}
    }),
    [PIDFormFields.busTxHex] : deserializeField({
      fieldName : PIDFormFields.busTxHex,
      value     : profile?.busTxHex,
      validator : {...Validators.HEX_10, helperText: hex10}
    }),
    [PIDFormFields.busRxHex] : deserializeField({
      fieldName : PIDFormFields.busRxHex,
      value     : profile?.busRxHex,
      validator : {...Validators.HEX_10, helperText: hex10}
    }),
    [PIDFormFields.initializeMessageHex] : deserializeField({
      fieldName : PIDFormFields.initializeMessageHex,
      value     : profile?.initializeMessageHex,
      validator : {...Validators.HEX_10, helperText: hex10}
    }),
    [PIDFormFields.keepAliveMessageHex] : deserializeField({
      fieldName : PIDFormFields.keepAliveMessageHex,
      value     : profile?.keepAliveMessageHex,
      validator : {...Validators.HEX_10, helperText: hex10}
    }),
    [PIDFormFields.exitMessageHex] : deserializeField({
      fieldName : PIDFormFields.exitMessageHex,
      value     : profile?.exitMessageHex,
      validator : {...Validators.HEX_10, helperText: hex10}
    }),
    [PIDFormFields.linearTransformationSlopeNumerator] : deserializeField({
      fieldName : PIDFormFields.linearTransformationSlopeNumerator,
      value :
        profile?.linearTransformationSlopeNumerator != null
          ? String(profile.linearTransformationSlopeNumerator)
          : '',
      required  : false,
      validator : {...Validators.NUMERIC, helperText: number}
    }),
    [PIDFormFields.linearTransformationSlopeDenominator] : deserializeField({
      fieldName : PIDFormFields.linearTransformationSlopeDenominator,
      value :
        profile?.linearTransformationSlopeDenominator != null
          ? String(profile.linearTransformationSlopeDenominator)
          : '',
      required  : false,
      validator : {...Validators.NUMERIC, helperText: number}
    }),
    [PIDFormFields.linearTransformationYIntercept] : deserializeField({
      fieldName : PIDFormFields.linearTransformationYIntercept,
      value :
        profile?.linearTransformationYIntercept != null
          ? String(profile.linearTransformationYIntercept)
          : '',
      required  : false,
      validator : {...Validators.NUMERIC, helperText: number}
    }),
    [PIDFormFields.firmwareParameterName] : deserializeField({
      fieldName : PIDFormFields.firmwareParameterName,
      value     : profile?.firmwareParameterName
    }),
    [PIDFormFields.formulaName] : deserializeField({
      fieldName : PIDFormFields.formulaName,
      value     : profile?.formulaName,
      required  : false
    }),
    [PIDFormFields.requestMessageHex] : deserializeField({
      fieldName : PIDFormFields.requestMessageHex,
      value     : profile?.requestMessageHex,
      validator : {...Validators.HEX_10, helperText: hex10}
    }),
    [PIDFormFields.requestMessageExtendedHex] : deserializeField({
      fieldName : PIDFormFields.requestMessageExtendedHex,
      value     : profile?.requestMessageExtendedHex,
      validator : {...Validators.HEX_12, helperText: hex12}
    }),
    [PIDFormFields.replyTimeout] : deserializeField({
      fieldName : PIDFormFields.replyTimeout,
      value :
        profile?.replyTimeoutCentiSecond != null
          ? String(profile.replyTimeoutCentiSecond)
          : '',
      validator : {...Validators.NUMERIC, helperText: number}
    }),
    [PIDFormFields.extendedRequestIdentifierHex] : deserializeField({
      fieldName : PIDFormFields.extendedRequestIdentifierHex,
      value     : profile?.extendedRequestIdentifierHex ?? DEFAULTS.hex4,
      validator : {...Validators.HEX_4, helperText: hex2}
    }),
    [PIDFormFields.extendedReplyIdentifierHex] : deserializeField({
      fieldName : PIDFormFields.extendedReplyIdentifierHex,
      value     : profile?.extendedReplyIdentifierHex ?? DEFAULTS.hex4,
      validator : {...Validators.HEX_4, helperText: hex2}
    }),
    [PIDFormFields.busAuthorizationKeyName] : deserializeField({
      fieldName : PIDFormFields.busAuthorizationKeyName,
      value     : profile?.busAuthorizationKeyName
    }),
    [PIDFormFields.busTypeExtensionName] : deserializeField({
      fieldName : PIDFormFields.busTypeExtensionName,
      value     : profile?.busTypeExtensionName
    })
  };
};

type Mode = 'CREATE' | 'EDIT';
type FieldType = 'text' | 'number' | 'hex10' | 'hex12' | 'hex4';
type FieldRule = { type: FieldType; requiredIn?: Mode[] };

const PID_FIELD_SCHEMA: Record<PIDFormFields, FieldRule> = {
  // General
  [PIDFormFields.firmwareParameterName] : {
    type       : 'text',
    requiredIn : ['CREATE', 'EDIT']
  },
  [PIDFormFields.busProfileName]        : {type: 'text', requiredIn: ['EDIT']},
  [PIDFormFields.busVersionName]        : {type: 'text', requiredIn: ['EDIT']},
  [PIDFormFields.customerRestrictionId] : {type: 'number', requiredIn: ['EDIT']},

  // Bus
  [PIDFormFields.busTypeName]          : {type: 'text', requiredIn: ['CREATE', 'EDIT']},
  [PIDFormFields.busTypeExtensionName] : {
    type       : 'text',
    requiredIn : ['CREATE', 'EDIT']
  },
  [PIDFormFields.busAuthorizationKeyName] : {type: 'text'},
  [PIDFormFields.busSpeed]                : {type: 'number', requiredIn: ['CREATE', 'EDIT']},

  // Data
  [PIDFormFields.byteStartPosition] : {type: 'number', requiredIn: ['CREATE', 'EDIT']},
  [PIDFormFields.byteLength]        : {type: 'number', requiredIn: ['CREATE', 'EDIT']},
  [PIDFormFields.bitStartPosition]  : {type: 'number', requiredIn: ['CREATE', 'EDIT']},
  [PIDFormFields.bitLength]         : {type: 'number', requiredIn: ['CREATE', 'EDIT']},

  // Communication
  [PIDFormFields.busTxHex]             : {type: 'hex10', requiredIn: ['CREATE', 'EDIT']},
  [PIDFormFields.busRxHex]             : {type: 'hex10', requiredIn: ['CREATE', 'EDIT']},
  [PIDFormFields.initializeMessageHex] : {
    type       : 'hex10',
    requiredIn : ['CREATE', 'EDIT']
  },
  [PIDFormFields.keepAliveMessageHex] : {
    type       : 'hex10',
    requiredIn : ['CREATE', 'EDIT']
  },
  [PIDFormFields.exitMessageHex]    : {type: 'hex10', requiredIn: ['CREATE', 'EDIT']},
  [PIDFormFields.requestMessageHex] : {
    type       : 'hex10',
    requiredIn : ['CREATE', 'EDIT']
  },
  [PIDFormFields.requestMessageExtendedHex] : {
    type       : 'hex12',
    requiredIn : ['CREATE', 'EDIT']
  },
  [PIDFormFields.replyTimeout]                 : {type: 'number', requiredIn: ['CREATE', 'EDIT']},
  [PIDFormFields.extendedRequestIdentifierHex] : {
    type       : 'hex4',
    requiredIn : ['CREATE', 'EDIT']
  },
  [PIDFormFields.extendedReplyIdentifierHex] : {
    type       : 'hex4',
    requiredIn : ['CREATE', 'EDIT']
  },

  // Transformation (optional)
  [PIDFormFields.formulaName]                          : {type: 'text'},
  [PIDFormFields.linearTransformationSlopeNumerator]   : {type: 'number'},
  [PIDFormFields.linearTransformationSlopeDenominator] : {type: 'number'},
  [PIDFormFields.linearTransformationYIntercept]       : {type: 'number'}
};

const RE_HEX4  = /^0x[0-9A-Fa-f]{2}$/;
const RE_HEX10 = /^0x[0-9A-Fa-f]{8}$/;
const RE_HEX12 = /^0x[0-9A-Fa-f]{10}$/;
const RE_NUM   = /^-?\d+(\.\d+)?$/;

const isRequiredIn = (rule: FieldRule, mode: Mode) =>
  rule.requiredIn?.includes(mode) ?? false;

export const buildValidatorsFromSchema = (mode: Mode) => {
  const validators: Record<string, (val: any) => boolean | object> = {};
  Object.entries(PID_FIELD_SCHEMA).forEach(([key, rule]) => {
    validators[key] = (val: any) => {
      const raw = val?.value == null ? '' : String(val.value).trim();
      if (isRequiredIn(rule, mode)) {
        if (raw === '') return false;
      } else {
        if (raw === '') return true;
      }
      switch (rule.type) {
        case 'number':
          return RE_NUM.test(raw);
        case 'hex4':
          return RE_HEX4.test(raw);
        case 'hex10':
          return RE_HEX10.test(raw);
        case 'hex12':
          return RE_HEX12.test(raw);
        default:
          return true;
      }
    };
  });
  return validators;
};

export function deriveVehicleContext(profile?: any) {
  if (!profile) {
    return null;
  }
  const {
    yearString,
    makeString,
    modelString,
    engineString = '',
    fuelString = ''
  } = profile || {};
  if (!yearString || !makeString || !modelString) {
    return null;
  }
  return {yearString, makeString, modelString, engineString, fuelString};
}
